import type React from "react";
import { useEffect } from "react";

import { Grid2 } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useProfileContext } from "contexts/ProfileContext";
import { Calendar } from "routes/calendar/components/Calendar";
import { UpcomingAppointments } from "routes/dashboard/default/components/UpcomingAppointments";
import { View } from "shared/containers/View";
import { MarketTypes } from "types";
import { useUnreadMessagesCount } from "utils/contexts/queries/useUnreadMessagesCount";
import { isUserMarket } from "utils/profile/profileHelper";

import { ActivePatients } from "../components/ActivePatients";
import { ActivityLogsTable } from "../components/ActivityLogsTable";
import { EarningsTable } from "../components/EarningsTable";
import { EarningsWidget, EarningsWidgetSE } from "../components/EarningsWidget";
import { EnrolledPatients } from "../components/EnrolledPatients";
import { EventCardsGrid } from "../components/EventCardsGrid/EventCardsGrid";
import { Commissions, Enrollments } from "../components/HcoTherapist";
import { MedicalNotesTable } from "../components/MedicalNotesTable";
import { PatientsFunctionality } from "../components/PatientsFunctionality";
import { PatientsPain } from "../components/PatientsPain";
import { Rating } from "../components/Rating";
import { ReadyForDischargeTable } from "../components/ReadyForDischargeTable";

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { unreadMessagesCount } = useUnreadMessagesCount();
  const location = useLocation();

  invariant(profile, "Profile should be defined");

  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.slice(1);
      const element = document.getElementById(elementId);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  // controlled by the master hcp
  const showEarnings = profile?.therapist_profile.branded_health_care_provider.commission;
  const isUS = isUserMarket(MarketTypes.US, profile);

  return (
    <View title={t("views.dashboard")} description={t("views.dashboard_description")} hideBreadcrumbs>
      <Container>
        {/* <BrazeBanner placementId="dashboard-top" /> */}
        <EventCardsGrid unreadMessagesCount={unreadMessagesCount} />
        <ContentWrapper data-testid="upcoming-appointments">
          {profile?.therapist_profile?.feature_flags?.includes("BOOKING_V2") ? (
            <Calendar interactive={false} fullScreen={false} navigateToCalendar showWidgetHeader />
          ) : (
            <UpcomingAppointments />
          )}
        </ContentWrapper>
        <ContentWrapper id="unsigned-medical-note-table" data-testid="unsigned-medical-note-table">
          <MedicalNotesTable />
        </ContentWrapper>
        {isUS && (
          <ContentWrapper id="activity-logs-table" data-testid="activity-logs-table">
            <ActivityLogsTable />
          </ContentWrapper>
        )}
        <ContentWrapper data-testid="ready-for-discharge-table">
          <ReadyForDischargeTable />
        </ContentWrapper>
        {profile?.therapist_profile?.feature_flags?.includes("HYBRID_ENROLLMENT_BONUS") && (
          <ContentWrapper data-testid="earnings-widget">
            <EarningsWidgetSE />
          </ContentWrapper>
        )}
        {!isUS && showEarnings && <EarningsTable />}
        {isUS &&
          profile?.therapist_profile?.feature_flags?.includes("BOOKING_IGNORE_ACCEPTING_PATIENTS") &&
          !profile?.health_care_organization && (
            <ContentWrapper data-testid="earnings-widget">
              <EarningsWidget />
            </ContentWrapper>
          )}

        <Grid2 container spacing="30px" columns={12}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Rating />
          </Grid2>

          {isUS && (
            <>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <ActivePatients />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 4 }}>
                <EnrolledPatients />
              </Grid2>
            </>
          )}

          <Grid2 size={{ xs: 12, md: isUS ? 6 : 4 }}>
            <PatientsPain />
          </Grid2>

          <Grid2 size={{ xs: 12, md: isUS ? 6 : 4 }}>
            <PatientsFunctionality />
          </Grid2>

          {isUS && profile?.health_care_organization && (
            <>
              <Grid2 size={12}>
                <Enrollments />
              </Grid2>
              <Grid2 size={12}>
                <Commissions />
              </Grid2>
            </>
          )}
        </Grid2>
      </Container>
    </View>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.S_30};
  margin-bottom: ${props => props.theme.spacing.S_20};
  ${props => props.theme.belowBreakpoint} {
    gap: ${props => props.theme.spacing.S_10};
  }
`;

const ContentWrapper = styled.div`
  scroll-margin-top: 40px;
`;
